import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";
import Http from "../../Common/Http";
// import ReactDataTable from '../../Common/ReactDataTable.jsx';

// Add CSS animation for loading spinner
const spinnerStyle = document.createElement("style");
spinnerStyle.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(spinnerStyle);

const SupplyChainAndProduction = () => {
  const navigate = useNavigate();
  const [rawMaterialsData, setRawMaterialsData] = useState([]);
  const [packingMaterialsData, setPackingMaterialsData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [filteredStockData, setFilteredStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState("monthly");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle product click to navigate to product ledger
  const handleProductClick = (product) => {
    navigate(
      `/product-ledger/:id?productId=${product.id}&productCode=${
        product.code
      }&productName=${encodeURIComponent(product.name)}`
    );
  };

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it as sort field with ascending direction
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort data function
  const sortData = (data, field, direction) => {
    if (!field) return data;

    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      // Handle different data types
      if (field === "stock_qty") {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (direction === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };
  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("Fetching stock data from API...");

        // Fetch stock data
        const stockResponse = await Http.get("/product-wise-stock");
        console.log("Stock API response:", stockResponse);

        if (stockResponse.data.status === "success") {
          console.log("Stock data received:", stockResponse.data.item);
          console.log("Number of products:", stockResponse.data.item.length);
          setStockData(stockResponse.data.item);
          setFilteredStockData(stockResponse.data.item);
        } else {
          console.log("API returned non-success status:", stockResponse.data);
          setStockData([]);
          setFilteredStockData([]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching supply chain data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Search filtering and sorting effect
  useEffect(() => {
    let filtered = stockData;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = stockData.filter(
        (product) =>
          (product.name &&
            product.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (product.code &&
            product.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (product.category_name &&
            product.category_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (product.unit_name &&
            product.unit_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply sorting
    if (sortField) {
      filtered = sortData(filtered, sortField, sortDirection);
    }

    setFilteredStockData(filtered);
  }, [searchTerm, stockData, sortField, sortDirection]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search form submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Search is handled by the useEffect above
  };

  // Chart data processing functions
  const getStockStatusData = () => {
    if (!stockData.length) return [];

    const inStock = stockData.filter(
      (product) => parseFloat(product.stock_qty || 0) > 10
    ).length;
    const lowStock = stockData.filter((product) => {
      const qty = parseFloat(product.stock_qty || 0);
      return qty <= 10 && qty > 5;
    }).length;
    const critical = stockData.filter(
      (product) => parseFloat(product.stock_qty || 0) <= 5
    ).length;

    return [
      { name: "In Stock", value: inStock, color: "#28a745" },
      { name: "Low Stock", value: lowStock, color: "#ffc107" },
      { name: "Critical", value: critical, color: "#dc3545" },
    ];
  };

  const getCategoryWiseData = () => {
    if (!stockData.length) return [];

    const categoryMap = {};
    stockData.forEach((product) => {
      const category = product.category_name || "Uncategorized";
      const qty = parseFloat(product.stock_qty || 0);

      if (!categoryMap[category]) {
        categoryMap[category] = { category, totalStock: 0, productCount: 0 };
      }
      categoryMap[category].totalStock += qty;
      categoryMap[category].productCount += 1;
    });

    return Object.values(categoryMap).sort(
      (a, b) => b.totalStock - a.totalStock
    );
  };

  // Memoized top products data to ensure proper re-rendering
  const topProductsData = useMemo(() => {
    console.log(
      "topProductsData useMemo called, stockData length:",
      stockData.length
    );
    console.log("stockData sample:", stockData.slice(0, 3));

    if (!stockData.length) {
      console.log("No stock data available, returning empty array");
      return [];
    }

    const topProducts = stockData
      .map((product) => ({
        name: product.name || "N/A",
        code: product.code || "N/A",
        stock: parseFloat(product.stock_qty || 0),
        unit: product.unit_name || "pcs",
      }))
      .sort((a, b) => b.stock - a.stock)
      .slice(0, 10);

    console.log("Top 10 products data:", topProducts);
    return topProducts;
  }, [stockData]);

  const getStockTrendData = () => {
    if (!stockData.length) return [];

    // Simulate trend data based on current stock levels
    const categories = [
      ...new Set(stockData.map((p) => p.category_name || "Uncategorized")),
    ];

    return categories.slice(0, 6).map((category) => {
      const categoryProducts = stockData.filter(
        (p) => (p.category_name || "Uncategorized") === category
      );
      const totalStock = categoryProducts.reduce(
        (sum, p) => sum + parseFloat(p.stock_qty || 0),
        0
      );
      const avgStock = totalStock / categoryProducts.length;

      return {
        category,
        current: Math.round(avgStock),
        projected: Math.round(avgStock * (0.8 + Math.random() * 0.4)), // Simulate variation
        target: Math.round(avgStock * 1.2),
      };
    });
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  const PRODUCT_TYPE_STYLES = {
    raw_materials: {
      backgroundColor: "#fed7d7",
      color: "#742a2a",
      border: "1px solid #feb2b2",
    },
    packing_materials: {
      backgroundColor: "#feebc8",
      color: "#7b341e",
      border: "1px solid #fbd38d",
    },
    finished_goods: {
      backgroundColor: "#c6f6d5",
      color: "#22543d",
      border: "1px solid #9ae6b4",
    },
  };


  return (
    <div>
      <style>{`
        .hover-row:hover {
          background-color: #f8f9fa !important;
          transform: scale(1.01);
          transition: all 0.2s ease;
        }
        .hover-row {
          transition: all 0.2s ease;
        }
      `}</style>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Supply Chain Dashboard</h5>
          <form
            id="searchBar"
            className="search-bar"
            onSubmit={handleSearchSubmit}
          >
            <div className="form-control-wrapper">
              <input
                type="search"
                className="form-control bd-0"
                placeholder="Search products by name, code, category, or unit..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <button id="searchBtn" className="btn btn-orange" type="submit">
              <i className="fa fa-search" />
            </button>
            {searchTerm && (
              <button
                type="button"
                className="btn btn-secondary ml-2"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <i className="fa fa-times" />
              </button>
            )}
          </form>
        </div>

        <div className="am-pagebody" style={{ padding: "10px" }}>
          <div className="row row-sm mg-t-20">
            <div className="col-md-12">
              <div className="card pd-20 pd-sm-40">
                {/* Enhanced Stock Summary Cards */}
                {stockData.length > 0 && (
                  <>
                    <style>
                      {`
                      .dashboard-stats-container {
                        padding: 0.75rem 0;
                      }
                      
                      .stats-card {
                        background: linear-gradient(135deg, var(--card-bg-start), var(--card-bg-end));
                        border: none;
                        border-radius: 12px;
                        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
                        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                        overflow: hidden;
                        position: relative;
                        backdrop-filter: blur(10px);
                        min-height: 100px;
                      }
                      
                      .stats-card::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        height: 3px;
                        background: linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0.8), rgba(255,255,255,0.3));
                        animation: shimmer 2s infinite;
                      }
                      
                      @keyframes shimmer {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(100%); }
                      }
                      
                      .stats-card:hover {
                        transform: translateY(-4px) scale(1.01);
                        box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);
                      }
                      
                      .stats-card.primary {
                        --card-bg-start: #667eea;
                        --card-bg-end: #764ba2;
                      }
                      
                      .stats-card.success {
                        --card-bg-start: #11998e;
                        --card-bg-end: #38ef7d;
                      }
                      
                      .stats-card.warning {
                        --card-bg-start: #f093fb;
                        --card-bg-end: #f5576c;
                      }
                      
                      .stats-card.danger {
                        --card-bg-start: #fc466b;
                        --card-bg-end: #3f5efb;
                      }
                      
                      .card-content {
                        padding: 1rem;
                        display: flex;
                        align-items: center;
                        position: relative;
                        z-index: 2;
                        height: 100%;
                      }
                      
                      .icon-wrapper {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 45px;
                        height: 45px;
                        background: rgba(255, 255, 255, 0.2);
                        border-radius: 50%;
                        backdrop-filter: blur(10px);
                        border: 2px solid rgba(255, 255, 255, 0.3);
                        transition: all 0.3s ease;
                        flex-shrink: 0;
                        margin-right: 1rem;
                      }
                      
                      .stats-card:hover .icon-wrapper {
                        transform: rotate(360deg) scale(1.05);
                        background: rgba(255, 255, 255, 0.3);
                      }
                      
                      .icon-wrapper i {
                        font-size: 1.2rem;
                        color: white;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                      }
                      
                      .text-content {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        text-align: left;
                      }
                      
                      .stats-number {
                        font-size: 1.6rem;
                        font-weight: 700;
                        color: white;
                        margin: 0 0 0.25rem 0;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                        line-height: 1;
                        animation: countUp 0.8s ease-out;
                      }
                      
                      @keyframes countUp {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                      }
                      
                      .stats-label {
                        font-size: 0.75rem;
                        font-weight: 500;
                        color: rgba(255, 255, 255, 0.9);
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        margin: 0;
                        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
                      }
                      
                      .stats-trend {
                        position: absolute;
                        top: 0.75rem;
                        right: 0.75rem;
                        background: rgba(255, 255, 255, 0.2);
                        border-radius: 12px;
                        padding: 0.2rem 0.5rem;
                        font-size: 0.65rem;
                        font-weight: 600;
                        color: white;
                        backdrop-filter: blur(5px);
                      }
                      
                      .floating-elements {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        overflow: hidden;
                        pointer-events: none;
                      }
                      
                      .floating-elements::before,
                      .floating-elements::after {
                        content: '';
                        position: absolute;
                        width: 50px;
                        height: 50px;
                        border-radius: 50%;
                        background: rgba(255, 255, 255, 0.06);
                        animation: float 6s ease-in-out infinite;
                      }
                      
                      .floating-elements::before {
                        top: -25px;
                        right: -25px;
                        animation-delay: 0s;
                      }
                      
                      .floating-elements::after {
                        bottom: -25px;
                        left: -25px;
                        animation-delay: 3s;
                      }
                      
                      @keyframes float {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-8px) rotate(180deg); }
                      }
                      
                      /* Laptop/Desktop optimized sizes */
                      @media (min-width: 992px) and (max-width: 1400px) {
                        .stats-card {
                          min-height: 95px;
                        }
                        .card-content {
                          padding: 0.9rem;
                        }
                        .icon-wrapper {
                          width: 42px;
                          height: 42px;
                          margin-right: 0.9rem;
                        }
                        .icon-wrapper i {
                          font-size: 1.1rem;
                        }
                        .stats-number {
                          font-size: 1.5rem;
                        }
                        .stats-label {
                          font-size: 0.7rem;
                        }
                        .stats-trend {
                          padding: 0.15rem 0.4rem;
                          font-size: 0.6rem;
                        }
                      }
                      
                      @media (max-width: 991px) {
                        .stats-card {
                          margin-bottom: 0.75rem;
                          min-height: 90px;
                        }
                        .card-content {
                          padding: 0.8rem;
                        }
                        .stats-number {
                          font-size: 1.4rem;
                        }
                        .icon-wrapper {
                          width: 40px;
                          height: 40px;
                          margin-right: 0.8rem;
                        }
                        .icon-wrapper i {
                          font-size: 1rem;
                        }
                        .stats-label {
                          font-size: 0.7rem;
                        }
                      }
                      
                      @media (max-width: 576px) {
                        .card-content {
                          flex-direction: column;
                          text-align: center;
                          padding: 0.75rem;
                        }
                        .icon-wrapper {
                          margin-right: 0;
                          margin-bottom: 0.5rem;
                          width: 35px;
                          height: 35px;
                        }
                        .icon-wrapper i {
                          font-size: 0.9rem;
                        }
                        .text-content {
                          text-align: center;
                        }
                        .stats-number {
                          font-size: 1.2rem;
                        }
                        .stats-label {
                          font-size: 0.65rem;
                        }
                      }
                    `}
                    </style>

                    <div className="dashboard-stats-container m-0">
                      <div className="row">
                        <div className="col-lg-3 col-md-6 col-sm-6 m-0">
                          <div className="card stats-card primary text-white m-0">
                            <div className="floating-elements"></div>
                            <div className="stats-trend">
                              <i className="fa fa-arrow-up"></i> Active
                            </div>
                            <div className="card-content">
                              <div className="icon-wrapper">
                                <i className="fa fa-cubes"></i>
                              </div>
                              <div className="text-content">
                                <div className="stats-number">
                                  {stockData.length}
                                </div>
                                <div className="stats-label">
                                  Total Products
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-6 m-0">
                          <div className="card stats-card success text-white m-0">
                            <div className="floating-elements"></div>
                            <div className="stats-trend">
                              <i className="fa fa-check"></i> Good
                            </div>
                            <div className="card-content">
                              <div className="icon-wrapper">
                                <i className="fa fa-check-circle"></i>
                              </div>
                              <div className="text-content">
                                <div className="stats-number">
                                  {
                                    stockData.filter((product) => {
                                      const currentStock = parseFloat(
                                        product.stock_qty || 0
                                      );
                                      return currentStock > 10;
                                    }).length
                                  }
                                </div>
                                <div className="stats-label">In Stock</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-6 m-0">
                          <div className="card stats-card warning text-white m-0">
                            <div className="floating-elements"></div>
                            <div className="stats-trend">
                              <i className="fa fa-exclamation"></i> Alert
                            </div>
                            <div className="card-content">
                              <div className="icon-wrapper">
                                <i className="fa fa-exclamation-triangle"></i>
                              </div>
                              <div className="text-content">
                                <div className="stats-number">
                                  {
                                    stockData.filter((product) => {
                                      const currentStock = parseFloat(
                                        product.stock_qty || 0
                                      );
                                      return (
                                        currentStock <= 10 && currentStock > 5
                                      );
                                    }).length
                                  }
                                </div>
                                <div className="stats-label">Low Stock</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-3 col-md-6 col-sm-6 m-0">
                          <div className="card stats-card danger text-white m-0">
                            <div className="floating-elements"></div>
                            <div className="stats-trend">
                              <i className="fa fa-bell"></i> Urgent
                            </div>
                            <div className="card-content">
                              <div className="icon-wrapper">
                                <i className="fa fa-exclamation-circle"></i>
                              </div>
                              <div className="text-content">
                                <div className="stats-number">
                                  {
                                    stockData.filter((product) => {
                                      const currentStock = parseFloat(
                                        product.stock_qty || 0
                                      );
                                      return currentStock <= 5;
                                    }).length
                                  }
                                </div>
                                <div className="stats-label">Critical</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Beautiful Card Container for Stock Management Table */}
          <div
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: "20px",
              boxShadow:
                "0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              overflow: "hidden",
              marginBottom: "30px",
              position: "relative",
            }}
          >
            {/* Card Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: "25px 30px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Background Pattern */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "200px",
                  height: "200px",
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                  borderRadius: "50%",
                  transform: "translate(50%, -50%)",
                }}
              ></div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <div>
                  <h3
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: "24px",
                      fontWeight: "700",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    <i
                      className="fas fa-warehouse mr-3"
                      style={{ fontSize: "20px" }}
                    ></i>
                    Stock Management
                  </h3>
                </div>

                <div
                  style={{ display: "flex", gap: "20px", alignItems: "center" }}
                >
                  <p
                    style={{
                      color: "rgba(255, 255, 255, 0.9)",
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: "400",
                    }}
                  >
                    Monitor and manage your product inventory levels
                  </p>
                </div>
              </div>
            </div>

            {/* Search Results Info */}
            {searchTerm && (
              <div
                style={{
                  background:
                    "linear-gradient(90deg, #e6fffa 0%, #f0fff4 100%)",
                  border: "1px solid #b2f5ea",
                  borderRadius: "0",
                  padding: "15px 30px",
                  margin: "0",
                  borderLeft: "4px solid #38b2ac",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <i
                      className="fas fa-info-circle"
                      style={{ color: "#38b2ac", fontSize: "16px" }}
                    ></i>
                    <span
                      style={{
                        color: "#234e52",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      Showing {filteredStockData.length} of {stockData.length}{" "}
                      products matching &quot;{searchTerm}&quot;
                    </span>
                  </div>
                  {filteredStockData.length === 0 && (
                    <button
                      className="btn btn-sm"
                      style={{
                        background: "#38b2ac",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <i className="fas fa-times mr-1"></i>
                      Clear search
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Card Body with Table */}
            <div style={{ padding: "0" }}>
              <div
                className="table-responsive"
                style={{
                  borderRadius: "0",
                  overflow: "hidden",
                  boxShadow: "none",
                }}
              >
                <table
                  className="table mb-0"
                  style={{ backgroundColor: "#fff" }}
                >
                  <thead
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                    }}
                  >
                    <tr>
                      <th
                        onClick={() => handleSort("code")}
                        style={{
                          border: "none",
                          padding: "12px 16px",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.3s ease",
                          position: "relative",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor =
                            "rgba(255,255,255,0.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        <i className="fas fa-barcode mr-2"></i>Code
                        {sortField === "code" && (
                          <i
                            className={`fas fa-sort-${
                              sortDirection === "asc" ? "up" : "down"
                            } ml-2`}
                            style={{ color: "#ffd700" }}
                          ></i>
                        )}
                        {sortField !== "code" && (
                          <i
                            className="fas fa-sort ml-2"
                            style={{ opacity: 0.5 }}
                          ></i>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort("name")}
                        style={{
                          border: "none",
                          padding: "12px 16px",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.3s ease",
                          position: "relative",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor =
                            "rgba(255,255,255,0.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        <i className="fas fa-box mr-2"></i>Product Name
                        {sortField === "name" && (
                          <i
                            className={`fas fa-sort-${
                              sortDirection === "asc" ? "up" : "down"
                            } ml-2`}
                            style={{ color: "#ffd700" }}
                          ></i>
                        )}
                        {sortField !== "name" && (
                          <i
                            className="fas fa-sort ml-2"
                            style={{ opacity: 0.5 }}
                          ></i>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort("stock_qty")}
                        style={{
                          border: "none",
                          padding: "12px 16px",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.3s ease",
                          position: "relative",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor =
                            "rgba(255,255,255,0.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        <i className="fas fa-warehouse mr-2"></i>Current Stock
                        {sortField === "stock_qty" && (
                          <i
                            className={`fas fa-sort-${
                              sortDirection === "asc" ? "up" : "down"
                            } ml-2`}
                            style={{ color: "#ffd700" }}
                          ></i>
                        )}
                        {sortField !== "stock_qty" && (
                          <i
                            className="fas fa-sort ml-2"
                            style={{ opacity: 0.5 }}
                          ></i>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort("unit")}
                        style={{
                          border: "none",
                          padding: "12px 16px",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.3s ease",
                          position: "relative",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor =
                            "rgba(255,255,255,0.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        <i className="fas fa-ruler mr-2"></i>Unit
                        {sortField === "unit" && (
                          <i
                            className={`fas fa-sort-${
                              sortDirection === "asc" ? "up" : "down"
                            } ml-2`}
                            style={{ color: "#ffd700" }}
                          ></i>
                        )}
                        {sortField !== "unit" && (
                          <i
                            className="fas fa-sort ml-2"
                            style={{ opacity: 0.5 }}
                          ></i>
                        )}
                      </th>
                      <th
                        onClick={() => handleSort("category")}
                        style={{
                          border: "none",
                          padding: "12px 16px",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.3s ease",
                          position: "relative",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor =
                            "rgba(255,255,255,0.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      >
                        <i className="fas fa-tags mr-2"></i>Category
                        {sortField === "category" && (
                          <i
                            className={`fas fa-sort-${
                              sortDirection === "asc" ? "up" : "down"
                            } ml-2`}
                            style={{ color: "#ffd700" }}
                          ></i>
                        )}
                        {sortField !== "category" && (
                          <i
                            className="fas fa-sort ml-2"
                            style={{ opacity: 0.5 }}
                          ></i>
                        )}
                      </th>
                      <th
                        style={{
                          border: "none",
                          padding: "12px 16px",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                        }}
                      >
                        <i className="fas fa-chart-line mr-2"></i>Type
                      </th>
                      <th
                        style={{
                          border: "none",
                          padding: "12px 16px",
                          fontWeight: "600",
                          fontSize: "11px",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                        }}
                      >
                        <i className="fas fa-chart-line mr-2"></i>Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStockData.length > 0 ? (
                      <>
                        {filteredStockData.map((product, index) => {
                          const currentStock = parseFloat(
                            product.stock_qty || 0
                          );
                          const isLowStock = currentStock <= 10; // Simple threshold for demo
                          const isCritical = currentStock <= 5; // Critical threshold

                          return (
                            <tr
                              key={index}
                              onClick={() => handleProductClick(product)}
                              style={{
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                borderBottom: "1px solid #f1f3f4",
                              }}
                              className="hover-row"
                              title="Click to view product ledger"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f8f9ff";
                                e.currentTarget.style.transform =
                                  "translateY(-2px)";
                                e.currentTarget.style.boxShadow =
                                  "0 4px 12px rgba(0,0,0,0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <td
                                style={{
                                  padding: "14px 16px",
                                  border: "none",
                                  verticalAlign: "middle",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "6px",
                                      background:
                                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "white",
                                      fontSize: "10px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {(product.code || "N/A")
                                      .substring(0, 2)
                                      .toUpperCase()}
                                  </div>
                                  <span
                                    style={{
                                      fontWeight: "600",
                                      color: "#2d3748",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {product.code || "N/A"}
                                  </span>
                                </div>
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  border: "none",
                                  verticalAlign: "middle",
                                }}
                              >
                                <div>
                                  <div
                                    style={{
                                      fontWeight: "600",
                                      color: "#2d3748",
                                      fontSize: "12px",
                                      marginBottom: "2px",
                                    }}
                                  >
                                    {product.name || "N/A"}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "10px",
                                      color: "#718096",
                                      fontWeight: "500",
                                    }}
                                  >
                                    ID: {product.id || "N/A"}
                                  </div>
                                </div>
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  border: "none",
                                  verticalAlign: "middle",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "6px",
                                      height: "6px",
                                      borderRadius: "50%",
                                      backgroundColor: isCritical
                                        ? "#e53e3e"
                                        : isLowStock
                                        ? "#ed8936"
                                        : "#38a169",
                                    }}
                                  ></div>
                                  <span
                                    style={{
                                      fontWeight: "700",
                                      fontSize: "13px",
                                      color: isCritical
                                        ? "#e53e3e"
                                        : isLowStock
                                        ? "#ed8936"
                                        : "#38a169",
                                    }}
                                  >
                                    {currentStock.toLocaleString()}
                                  </span>
                                </div>
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  border: "none",
                                  verticalAlign: "middle",
                                }}
                              >
                                <span
                                  style={{
                                    color: "#4a5568",
                                    fontWeight: "500",
                                    fontSize: "11px",
                                  }}
                                >
                                  {product.unit_name || "pcs"}
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  border: "none",
                                  verticalAlign: "middle",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "4px 8px",
                                    borderRadius: "12px",
                                    backgroundColor: "#e6fffa",
                                    color: "#234e52",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    border: "1px solid #b2f5ea",
                                  }}
                                >
                                  <i
                                    className="fas fa-tag mr-1"
                                    style={{ fontSize: "8px" }}
                                  ></i>
                                  {product.category_name || "N/A"}
                                </span>
                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  border: "none",
                                  verticalAlign: "middle",
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "4px 8px",
                                    borderRadius: "12px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    ...(PRODUCT_TYPE_STYLES[product.type] || {
                                      backgroundColor: "#edf2f7",
                                      color: "#4a5568",
                                      border: "1px solid #e2e8f0",
                                    }),
                                  }}
                                >
                                  {product.type
                                    ? product.type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                                    : "N/A"}
                                </span>

                              </td>
                              <td
                                style={{
                                  padding: "14px 16px",
                                  border: "none",
                                  verticalAlign: "middle",
                                }}
                              >
                                {isCritical ? (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      padding: "5px 10px",
                                      borderRadius: "15px",
                                      backgroundColor: "#fed7d7",
                                      color: "#c53030",
                                      fontSize: "10px",
                                      fontWeight: "700",
                                      border: "1px solid #feb2b2",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.3px",
                                    }}
                                  >
                                    <i
                                      className="fas fa-exclamation-triangle mr-1"
                                      style={{ fontSize: "8px" }}
                                    ></i>
                                    Critical
                                  </span>
                                ) : isLowStock ? (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      padding: "5px 10px",
                                      borderRadius: "15px",
                                      backgroundColor: "#feebc8",
                                      color: "#c05621",
                                      fontSize: "10px",
                                      fontWeight: "700",
                                      border: "1px solid #fbd38d",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.3px",
                                    }}
                                  >
                                    <i
                                      className="fas fa-exclamation-circle mr-1"
                                      style={{ fontSize: "8px" }}
                                    ></i>
                                    Low Stock
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      padding: "5px 10px",
                                      borderRadius: "15px",
                                      backgroundColor: "#c6f6d5",
                                      color: "#22543d",
                                      fontSize: "10px",
                                      fontWeight: "700",
                                      border: "1px solid #9ae6b4",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.3px",
                                    }}
                                  >
                                    <i
                                      className="fas fa-check-circle mr-1"
                                      style={{ fontSize: "8px" }}
                                    ></i>
                                    In Stock
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          style={{
                            padding: "60px 20px",
                            border: "none",
                            textAlign: "center",
                          }}
                        >
                          {loading ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "20px",
                              }}
                            >
                              <div
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  border: "4px solid #e2e8f0",
                                  borderTop: "4px solid #667eea",
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite",
                                }}
                              ></div>
                              <div
                                style={{
                                  color: "#4a5568",
                                  fontSize: "16px",
                                  fontWeight: "500",
                                }}
                              >
                                Loading product stock data...
                              </div>
                            </div>
                          ) : searchTerm ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "16px",
                              }}
                            >
                              <div
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  borderRadius: "50%",
                                  backgroundColor: "#f7fafc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid #e2e8f0",
                                }}
                              >
                                <i
                                  className="fas fa-search"
                                  style={{
                                    fontSize: "24px",
                                    color: "#a0aec0",
                                  }}
                                ></i>
                              </div>
                              <div>
                                <div
                                  style={{
                                    color: "#2d3748",
                                    fontSize: "18px",
                                    fontWeight: "600",
                                    marginBottom: "8px",
                                  }}
                                >
                                  No products found
                                </div>
                                <div
                                  style={{
                                    color: "#718096",
                                    fontSize: "14px",
                                  }}
                                >
                                  No products match &quot;{searchTerm}&quot;
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "16px",
                              }}
                            >
                              <div
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  borderRadius: "50%",
                                  backgroundColor: "#f7fafc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid #e2e8f0",
                                }}
                              >
                                <i
                                  className="fas fa-box-open"
                                  style={{
                                    fontSize: "24px",
                                    color: "#a0aec0",
                                  }}
                                ></i>
                              </div>
                              <div>
                                <div
                                  style={{
                                    color: "#2d3748",
                                    fontSize: "18px",
                                    fontWeight: "600",
                                    marginBottom: "8px",
                                  }}
                                >
                                  No stock data available
                                </div>
                                <div
                                  style={{
                                    color: "#718096",
                                    fontSize: "14px",
                                  }}
                                >
                                  Product stock information will appear here
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Product-wise Stock Table */}
          <div className="row row-sm mg-t-20">
            <div className="col-md-12">
              <div className="card pd-20 pd-sm-40">
                {/* Charts Section */}
                {stockData.length > 0 && (
                  <div className="row mt-4">
                    {/* Stock Status Distribution Pie Chart */}
                    <div className="col-md-4">
                      <div className="card modern-chart-card">
                        <style>
                          {`
                            .modern-chart-card {
                              background: linear-gradient(145deg, #ffffff, #f8f9fa);
                              border: none;
                              border-radius: 20px;
                              box-shadow: 
                                0 10px 30px rgba(0, 0, 0, 0.1),
                                0 1px 8px rgba(0, 0, 0, 0.06);
                              overflow: hidden;
                              position: relative;
                              transition: all 0.3s ease;
                            }
                            
                            .modern-chart-card::before {
                              content: '';
                              position: absolute;
                              top: 0;
                              left: 0;
                              right: 0;
                              height: 4px;
                              background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
                              background-size: 300% 100%;
                              animation: gradientShift 3s ease infinite;
                            }
                            
                            @keyframes gradientShift {
                              0%, 100% { background-position: 0% 50%; }
                              50% { background-position: 100% 50%; }
                            }
                            
                            .modern-chart-card:hover {
                              transform: translateY(-5px);
                              box-shadow: 
                                0 20px 40px rgba(0, 0, 0, 0.15),
                                0 5px 15px rgba(0, 0, 0, 0.08);
                            }
                            
                            .modern-chart-header {
                              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                              border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                              padding: 1.5rem;
                              position: relative;
                            }
                            
                            .modern-chart-title {
                              font-size: 1.1rem;
                              font-weight: 600;
                              color: #2c3e50;
                              margin: 0;
                              display: flex;
                              align-items: center;
                              gap: 0.5rem;
                            }
                            
                            .chart-icon {
                              width: 24px;
                              height: 24px;
                              background: linear-gradient(135deg, #667eea, #764ba2);
                              border-radius: 50%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              color: white;
                              font-size: 0.8rem;
                            }
                            
                            .modern-chart-body {
                              padding: 2rem;
                              background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                              position: relative;
                            }
                            
                            .chart-container {
                              position: relative;
                              background: radial-gradient(circle at center, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.5) 100%);
                              border-radius: 15px;
                              padding: 1rem;
                              box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.05);
                            }
                            
                            .chart-legend {
                              display: flex;
                              justify-content: center;
                              flex-wrap: wrap;
                              gap: 1rem;
                              margin-top: 1.5rem;
                              padding-top: 1.5rem;
                              border-top: 1px solid rgba(0, 0, 0, 0.1);
                            }
                            
                            .legend-item {
                              display: flex;
                              align-items: center;
                              gap: 0.5rem;
                              padding: 0.5rem 1rem;
                              background: rgba(255, 255, 255, 0.8);
                              border-radius: 25px;
                              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                              transition: all 0.3s ease;
                              font-size: 0.85rem;
                              font-weight: 500;
                            }
                            
                            .legend-item:hover {
                              transform: translateY(-2px);
                              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                            }
                            
                            .legend-color {
                              width: 12px;
                              height: 12px;
                              border-radius: 50%;
                              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                            }
                            
                            .floating-particles {
                              position: absolute;
                              top: 0;
                              left: 0;
                              right: 0;
                              bottom: 0;
                              pointer-events: none;
                              overflow: hidden;
                            }
                            
                            .floating-particles::before,
                            .floating-particles::after {
                              content: '';
                              position: absolute;
                              width: 60px;
                              height: 60px;
                              border-radius: 50%;
                              background: linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
                              animation: floatParticles 8s ease-in-out infinite;
                            }
                            
                            .floating-particles::before {
                              top: 10%;
                              right: 10%;
                              animation-delay: 0s;
                            }
                            
                            .floating-particles::after {
                              bottom: 10%;
                              left: 10%;
                              animation-delay: 4s;
                            }
                            
                            @keyframes floatParticles {
                              0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); opacity: 0.3; }
                              50% { transform: translateY(-20px) rotate(180deg) scale(1.1); opacity: 0.6; }
                            }
                          `}
                        </style>

                        <div className="floating-particles"></div>
                        <div className="modern-chart-header">
                          <h6 className="modern-chart-title">
                            <div className="chart-icon">
                              <i className="fa fa-chart-pie"></i>
                            </div>
                            Stock Status Distribution
                          </h6>
                        </div>
                        <div className="modern-chart-body">
                          <div className="chart-container">
                            <ResponsiveContainer width="100%" height={320}>
                              <PieChart>
                                <defs>
                                  <linearGradient
                                    id="inStockGradient"
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="100%"
                                  >
                                    <stop offset="0%" stopColor="#11998e" />
                                    <stop offset="100%" stopColor="#38ef7d" />
                                  </linearGradient>
                                  <linearGradient
                                    id="lowStockGradient"
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="100%"
                                  >
                                    <stop offset="0%" stopColor="#f093fb" />
                                    <stop offset="100%" stopColor="#f5576c" />
                                  </linearGradient>
                                  <linearGradient
                                    id="criticalGradient"
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="100%"
                                  >
                                    <stop offset="0%" stopColor="#fc466b" />
                                    <stop offset="100%" stopColor="#3f5efb" />
                                  </linearGradient>
                                  <linearGradient
                                    id="outOfStockGradient"
                                    x1="0%"
                                    y1="0%"
                                    x2="100%"
                                    y2="100%"
                                  >
                                    <stop offset="0%" stopColor="#667eea" />
                                    <stop offset="100%" stopColor="#764ba2" />
                                  </linearGradient>
                                </defs>
                                <Pie
                                  data={getStockStatusData()}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, value, percent }) =>
                                    value > 0
                                      ? `${(percent * 100).toFixed(1)}%`
                                      : ""
                                  }
                                  outerRadius={100}
                                  innerRadius={40}
                                  fill="#8884d8"
                                  dataKey="value"
                                  stroke="rgba(255, 255, 255, 0.8)"
                                  strokeWidth={3}
                                >
                                  {getStockStatusData().map((entry, index) => {
                                    const gradientMap = {
                                      "In Stock": "url(#inStockGradient)",
                                      "Low Stock": "url(#lowStockGradient)",
                                      Critical: "url(#criticalGradient)",
                                      "Out of Stock":
                                        "url(#outOfStockGradient)",
                                    };
                                    return (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={
                                          gradientMap[entry.name] || entry.color
                                        }
                                      />
                                    );
                                  })}
                                </Pie>
                                <Tooltip
                                  contentStyle={{
                                    background:
                                      "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 249, 250, 0.95))",
                                    border: "none",
                                    borderRadius: "12px",
                                    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
                                    backdropFilter: "blur(10px)",
                                    fontSize: "0.9rem",
                                    fontWeight: "500",
                                  }}
                                  formatter={(value, name) => [
                                    `${value} products`,
                                    name,
                                  ]}
                                />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div className="chart-legend">
                            {getStockStatusData().map(
                              (entry, index) =>
                                entry.value > 0 && (
                                  <div key={index} className="legend-item">
                                    <div
                                      className="legend-color"
                                      style={{ background: entry.color }}
                                    ></div>
                                    <span>
                                      {entry.name}: {entry.value}
                                    </span>
                                  </div>
                                )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-8">
                      <div className="card modern-bar-chart-card">
                        <style>
                          {`
                            .modern-bar-chart-card {
                              background: linear-gradient(145deg, #ffffff, #f8f9fa);
                              border: none;
                              border-radius: 20px;
                              box-shadow: 
                                0 10px 30px rgba(0, 0, 0, 0.1),
                                0 1px 8px rgba(0, 0, 0, 0.06);
                              overflow: hidden;
                              position: relative;
                              transition: all 0.3s ease;
                            }
                            
                            .modern-bar-chart-card::before {
                              content: '';
                              position: absolute;
                              top: 0;
                              left: 0;
                              right: 0;
                              height: 4px;
                              background: linear-gradient(90deg, #4facfe, #00f2fe, #43e97b, #38f9d7);
                              background-size: 300% 100%;
                              animation: barGradientShift 4s ease infinite;
                            }
                            
                            @keyframes barGradientShift {
                              0%, 100% { background-position: 0% 50%; }
                              50% { background-position: 100% 50%; }
                            }
                            
                            .modern-bar-chart-card:hover {
                              transform: translateY(-5px);
                              box-shadow: 
                                0 20px 40px rgba(0, 0, 0, 0.15),
                                0 5px 15px rgba(0, 0, 0, 0.08);
                            }
                            
                            .modern-bar-header {
                              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                              border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                              padding: 1.5rem;
                              position: relative;
                            }
                            
                            .modern-bar-title {
                              font-size: 1.1rem;
                              font-weight: 600;
                              color: #2c3e50;
                              margin: 0;
                              display: flex;
                              align-items: center;
                              gap: 0.5rem;
                            }
                            
                            .bar-chart-icon {
                              width: 24px;
                              height: 24px;
                              background: linear-gradient(135deg, #4facfe, #00f2fe);
                              border-radius: 50%;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              color: white;
                              font-size: 0.8rem;
                            }
                            
                            .modern-bar-body {
                              padding: 2rem;
                              background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                              position: relative;
                            }
                            
                            .bar-chart-container {
                              position: relative;
                              background: radial-gradient(circle at center, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.5) 100%);
                              border-radius: 15px;
                              padding: 1.5rem;
                              box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.05);
                            }
                            
                            .floating-bar-particles {
                              position: absolute;
                              top: 0;
                              left: 0;
                              right: 0;
                              bottom: 0;
                              pointer-events: none;
                              overflow: hidden;
                            }
                            
                            .floating-bar-particles::before,
                            .floating-bar-particles::after {
                              content: '';
                              position: absolute;
                              width: 40px;
                              height: 40px;
                              border-radius: 50%;
                              background: linear-gradient(45deg, rgba(79, 172, 254, 0.1), rgba(0, 242, 254, 0.1));
                              animation: floatBarParticles 10s ease-in-out infinite;
                            }
                            
                            .floating-bar-particles::before {
                              top: 15%;
                              right: 15%;
                              animation-delay: 0s;
                            }
                            
                            .floating-bar-particles::after {
                              bottom: 15%;
                              left: 15%;
                              animation-delay: 5s;
                            }
                            
                            @keyframes floatBarParticles {
                              0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); opacity: 0.2; }
                              50% { transform: translateY(-15px) rotate(180deg) scale(1.2); opacity: 0.5; }
                            }
                            
                            .chart-stats-overlay {
                              position: absolute;
                              top: 1rem;
                              right: 1rem;
                              background: rgba(255, 255, 255, 0.9);
                              backdrop-filter: blur(10px);
                              border-radius: 12px;
                              padding: 0.75rem 1rem;
                              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
                              font-size: 0.8rem;
                              color: #6c757d;
                              font-weight: 500;
                            }
                          `}
                        </style>
                        <div className="floating-bar-particles"></div>
                        <div className="modern-bar-header">
                          <h6 className="modern-bar-title">
                            <div className="bar-chart-icon">
                              <i className="fa fa-chart-bar"></i>
                            </div>
                            Top 10 Products by Stock
                          </h6>
                        </div>
                        <div className="modern-bar-body">
                          <div className="bar-chart-container">
                            <div className="chart-stats-overlay">
                              📊 Stock Analysis
                            </div>
                            {topProductsData.length === 0 ? (
                              <div
                                className="d-flex justify-content-center align-items-center"
                                style={{ height: "420px" }}
                              >
                                <div className="text-center">
                                  <div className="mb-3">
                                    <i className="fa fa-chart-bar fa-3x text-muted"></i>
                                  </div>
                                  <h6 className="text-muted">
                                    No stock data available
                                  </h6>
                                  <p className="text-muted small">
                                    Stock data will appear here once loaded
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <ResponsiveContainer width="100%" height={420}>
                                <BarChart
                                  data={topProductsData}
                                  margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 80,
                                  }}
                                >
                                  <defs>
                                    <linearGradient
                                      id="barGradient"
                                      x1="0%"
                                      y1="0%"
                                      x2="0%"
                                      y2="100%"
                                    >
                                      <stop offset="0%" stopColor="#4facfe" />
                                      <stop offset="50%" stopColor="#00f2fe" />
                                      <stop offset="100%" stopColor="#43e97b" />
                                    </linearGradient>
                                    <linearGradient
                                      id="barGradientHover"
                                      x1="0%"
                                      y1="0%"
                                      x2="0%"
                                      y2="100%"
                                    >
                                      <stop offset="0%" stopColor="#667eea" />
                                      <stop offset="50%" stopColor="#764ba2" />
                                      <stop offset="100%" stopColor="#f093fb" />
                                    </linearGradient>
                                    <filter id="barShadow">
                                      <feDropShadow
                                        dx="2"
                                        dy="2"
                                        stdDeviation="3"
                                        floodOpacity="0.3"
                                      />
                                    </filter>
                                  </defs>
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="rgba(0, 0, 0, 0.1)"
                                    horizontal={true}
                                    vertical={false}
                                  />
                                  <XAxis
                                    type="category"
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{
                                      fontSize: 9,
                                      fill: "#495057",
                                      fontWeight: 600,
                                    }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                  />
                                  <YAxis
                                    type="number"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{
                                      fontSize: 11,
                                      fill: "#6c757d",
                                      fontWeight: 500,
                                    }}
                                    tickFormatter={(value) =>
                                      value.toLocaleString()
                                    }
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      background:
                                        "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 249, 250, 0.95))",
                                      border: "none",
                                      borderRadius: "12px",
                                      boxShadow:
                                        "0 8px 25px rgba(0, 0, 0, 0.15)",
                                      backdropFilter: "blur(10px)",
                                      fontSize: "0.9rem",
                                      fontWeight: "500",
                                    }}
                                    cursor={{
                                      fill: "rgba(79, 172, 254, 0.1)",
                                      stroke: "rgba(79, 172, 254, 0.3)",
                                      strokeWidth: 2,
                                    }}
                                    formatter={(value, name) => [
                                      `${value.toLocaleString()} units`,
                                      "Stock Quantity",
                                    ]}
                                    labelFormatter={(label) => {
                                      const product = topProductsData.find(
                                        (p) => p.name === label
                                      );
                                      return product
                                        ? `📦 ${product.name} (${product.code})`
                                        : label;
                                    }}
                                  />
                                  <Bar
                                    dataKey="stock"
                                    fill="url(#barGradient)"
                                    radius={[8, 8, 0, 0]}
                                    filter="url(#barShadow)"
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Beautiful Card Container for Stock Management Table */}
              
              </div>
            </div>
          </div>
        </div>

        <div className="am-footer">
          <span>
            Copyright ©. All Rights Reserved.{" "}
            <a href="http://limerickbd.com/">Limerick Resources Limited</a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default SupplyChainAndProduction;
